import React, { useEffect, useRef, useState } from 'react';
import SiteFooter from '../components/footer/SiteFooter';
import SiteHeader from '../components/navigation/SiteHeader';
import SkipLink from '../components/ui/SkipLink';
import { verifyLastNutPassword } from '../security/passwordGate';
import styles from './LastNutPage.module.css';

const START_TIME = new Date('2026-08-06T12:35:20-07:00').getTime();

function getElapsedParts(now) {
  let remainingSeconds = Math.max(0, Math.floor((now - START_TIME) / 1000));
  const days = Math.floor(remainingSeconds / 86400);
  remainingSeconds %= 86400;
  const hours = Math.floor(remainingSeconds / 3600);
  remainingSeconds %= 3600;
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;

  return [
    { label: 'Days', value: String(days) },
    { label: 'Hours', value: String(hours).padStart(2, '0') },
    { label: 'Minutes', value: String(minutes).padStart(2, '0') },
    { label: 'Seconds', value: String(seconds).padStart(2, '0') },
  ];
}

function FlipValue({ label, value }) {
  const previousValueRef = useRef(value);
  const previousValue = previousValueRef.current;
  const hasChanged = previousValue !== value;
  const testId = label.toLowerCase();

  useEffect(() => {
    previousValueRef.current = value;
  }, [value]);

  return (
    <dd className={styles.value} data-testid={`elapsed-${testId}`}>
      <span className={styles.screenReader}>{value}</span>
      <span aria-hidden="true" className={`${styles.half} ${styles.topHalf}`}>
        <span className={styles.digit}>{value}</span>
      </span>
      <span aria-hidden="true" className={`${styles.half} ${styles.bottomHalf}`}>
        <span className={styles.digit}>{value}</span>
      </span>
      {hasChanged ? (
        <>
          <span
            aria-hidden="true"
            className={`${styles.half} ${styles.topHalf} ${styles.flipTop}`}
            data-testid={`flip-previous-${testId}`}
            key={`top-${previousValue}-${value}`}
          >
            <span className={styles.digit}>{previousValue}</span>
          </span>
          <span
            aria-hidden="true"
            className={`${styles.half} ${styles.bottomHalf} ${styles.holdBottom}`}
            key={`hold-${previousValue}-${value}`}
          >
            <span className={styles.digit}>{previousValue}</span>
          </span>
          <span
            aria-hidden="true"
            className={`${styles.half} ${styles.bottomHalf} ${styles.flipBottom}`}
            data-testid={`flip-current-${testId}`}
            key={`bottom-${previousValue}-${value}`}
          >
            <span className={styles.digit}>{value}</span>
          </span>
        </>
      ) : null}
    </dd>
  );
}

export default function LastNutPage() {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const elapsed = getElapsedParts(now);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();
    if (isChecking || !password) return;

    setIsChecking(true);
    setError('');

    try {
      const isValid = await verifyLastNutPassword(password);
      setPassword('');
      if (isValid) setIsUnlocked(true);
      else setError('That code is not right.');
    } catch {
      setPassword('');
      setError('Unable to check the code in this browser.');
    } finally {
      setIsChecking(false);
    }
  }

  return (
    <>
      <SkipLink />
      <SiteHeader />
      <main className={styles.main} id="main-content">
        {!isUnlocked ? (
          <section className={styles.gate} aria-label="Password access">
            <form aria-busy={isChecking} className={styles.form} onSubmit={handleSubmit}>
              <label htmlFor="last-nut-password">Access code</label>
              <input
                aria-describedby="last-nut-message"
                autoCapitalize="none"
                autoComplete="current-password"
                autoFocus
                disabled={isChecking}
                id="last-nut-password"
                name="access-code"
                onChange={(event) => {
                  setPassword(event.target.value);
                  if (error) setError('');
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleSubmit(event);
                }}
                spellCheck="false"
                type="password"
                value={password}
              />
              <p className={styles.formMessage} id="last-nut-message" role="status">
                {error}
              </p>
            </form>
          </section>
        ) : (
          <section className={styles.counter} aria-labelledby="elapsed-title">
            <h1 id="elapsed-title">Time elapsed</h1>
            <dl className={styles.elapsed} role="timer" aria-live="off">
              {elapsed.map(({ label, value }) => (
                <div className={styles.unit} key={label}>
                  <dt className={styles.label}>{label}</dt>
                  <FlipValue label={label} value={value} />
                </div>
              ))}
            </dl>
          </section>
        )}
      </main>
      <SiteFooter />
    </>
  );
}
